import { useState, useEffect, useCallback, useRef } from 'react'
import type { MouseEvent } from 'react'
import Link from 'next/link'
import { Image } from '@educator/components/Image'
import { getBasePath } from '@educator/config/basePath'
import { smoothScrollToElement } from '@educator/utils/smoothScrollTo'
import { useTranslation } from '@mc/i18n'
import { MobileMenuButton } from '../../../components/TopNavigation/MobileMenu'
import { useEnrollmentCtaConfig } from '../../../hooks/useEnrollmentCtaConfig'
import { TrackClick, useFireEvent } from '../../../tracking/educatorTracking'
import topNavClasses from '../../../components/TopNavigation/TopNavigation.module.scss'

/** Offset to account for fixed nav bar (top position + nav height + padding) */
const SCROLL_OFFSET = 100

/** Default navigation section order (matches enrollment page layout) */
const DEFAULT_NAV_SECTIONS = ['faculty', 'program', 'experience'] as const
type NavSection = 'faculty' | 'program' | 'experience'

/**
 * NavBar variant controls color scheme:
 * - 'dark': White text/borders on dark backgrounds (Enrollment page)
 * - 'light': Black text/borders on light backgrounds (Performance page)
 */
type NavBarVariant = 'dark' | 'light'

interface NavBarProps {
  /** Color variant: 'dark' for white text (enrollment), 'light' for black text (performance) */
  variant?: NavBarVariant
  /** Order of nav section links (L→R should match top→bottom on the page) */
  sectionOrder?: readonly NavSection[]
  /** Shift the fixed desktop nav down to account for the urgency banner */
  withBanner?: boolean
  hideDesktopNav?: boolean
  /** Drop the enrollment CTA from the phone bar, for pages that sell something
   *  of their own and have no business pushing the programme. */
  hideEnrollmentCta?: boolean
  /** Sticky phone bar. Drops the difference blend, which inverts over
   *  scrolling artwork. */
  stickyMobileNav?: boolean
}

export const NavBar = ({
  variant = 'dark',
  sectionOrder = DEFAULT_NAV_SECTIONS,
  withBanner = false,
  hideDesktopNav = false,
  hideEnrollmentCta = false,
  stickyMobileNav = false,
}: NavBarProps) => {
  const { t } = useTranslation('@mc/educator')
  const fireEvent = useFireEvent()
  const { primaryCta, secondaryCta, isPrimaryLogIn } = useEnrollmentCtaConfig()
  // Nav layout is identity-based (per Figma), NOT primary/secondary: the
  // enrollment CTA (Apply for Free / Join Waitlist) is plain on the left, and
  // Log In is bordered on the right — in every state. Only the enrollment CTA
  // fires educatorPurchaseClick.
  const loginCta = isPrimaryLogIn ? primaryCta : secondaryCta
  const enrollmentCta = isPrimaryLogIn ? secondaryCta : primaryCta
  const basePath = getBasePath()
  const [activeSection, setActiveSection] = useState<NavSection | null>(null)
  const [desktopNavHidden, setDesktopNavHidden] = useState(false)
  const lastScrollY = useRef(0)

  const isLight = variant === 'light'
  const textColor = isLight ? 'text-black' : 'text-white'
  const borderColor = isLight ? 'border-black' : 'border-white'
  const activeBg = isLight ? 'bg-black' : 'bg-white'
  const desktopBg = 'bg-[rgba(0,0,0,0.15)] backdrop-blur-[15px]'
  const linkColorStyle = { color: isLight ? '#000000' : '#ffffff' } as const

  /** Determine which section is currently in view */
  const updateActiveSection = useCallback(() => {
    const viewportMiddle = window.scrollY + window.innerHeight / 3

    const isInView = (element: HTMLElement | null): boolean => {
      if (!element) return false
      const rect = element.getBoundingClientRect()
      const sectionTop = rect.top + window.scrollY
      const sectionBottom = sectionTop + rect.height
      return viewportMiddle >= sectionTop && viewportMiddle < sectionBottom
    }

    const currentSection = sectionOrder.find((sectionId) => {
      if (sectionId === 'faculty') {
        return (
          isInView(document.getElementById('faculty-intro-desktop')) ||
          isInView(document.getElementById('faculty-intro-mobile')) ||
          isInView(document.getElementById('faculty'))
        )
      }
      return isInView(document.getElementById(sectionId))
    })

    setActiveSection(currentSection ?? null)
  }, [sectionOrder])

  useEffect(() => {
    if (hideDesktopNav) return () => {}
    updateActiveSection()
    window.addEventListener('scroll', updateActiveSection, { passive: true })
    return () => window.removeEventListener('scroll', updateActiveSection)
  }, [updateActiveSection, hideDesktopNav])

  /** Hide desktop nav on scroll down, show on scroll up */
  useEffect(() => {
    if (hideDesktopNav) return () => {}
    const handleScrollDirection = () => {
      const currentY = window.scrollY
      if (currentY > lastScrollY.current && currentY > SCROLL_OFFSET) {
        setDesktopNavHidden(true)
      } else if (currentY < lastScrollY.current) {
        setDesktopNavHidden(false)
      }
      lastScrollY.current = currentY
    }

    window.addEventListener('scroll', handleScrollDirection, { passive: true })
    return () => window.removeEventListener('scroll', handleScrollDirection)
  }, [hideDesktopNav])

  const handleNavClick = (event: MouseEvent<HTMLButtonElement>) => {
    const targetId = event.currentTarget.dataset.target

    if (!targetId) return

    // Faculty: scroll to "IMPOSSIBLE FACULTY" intro on Enrollment page; fall back to faculty section (e.g. Performance page)
    const isDesktop = typeof window !== 'undefined' && window.innerWidth >= 768
    let scrollTargetId = targetId
    if (targetId === 'faculty') {
      scrollTargetId = isDesktop
        ? 'faculty-intro-desktop'
        : 'faculty-intro-mobile'
    }
    let target = document.getElementById(scrollTargetId)
    if (!target && targetId === 'faculty') {
      target = document.getElementById('faculty')
    }

    if (target) {
      smoothScrollToElement(target, SCROLL_OFFSET)
    }
  }

  return (
    <>
      <header
        className={`left-0 right-0 top-0 z-50 md:hidden ${
          stickyMobileNav ? 'fixed bg-edu-bg-primary' : 'absolute'
        }`}
        style={stickyMobileNav ? undefined : { mixBlendMode: 'difference' }}
      >
        <nav>
          <div className='flex items-center justify-between p-[15px]'>
            <TrackClick
              event='educatorSignClick'
              action='masterclass'
              location='sticky nav'
            >
              <button
                type='button'
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                aria-label={t('enrollment.nav.logoAlt')}
                className='cursor-pointer flex items-center justify-center'
              >
                <Image
                  src={`${basePath}/images/executive-logo.svg`}
                  alt={t('enrollment.nav.logoAlt')}
                  width={102}
                  height={30}
                  style={isLight ? { filter: 'brightness(0)' } : undefined}
                />
              </button>
            </TrackClick>

            <div className='flex items-center gap-[6px]'>
              {!hideEnrollmentCta && (
                <Link
                  href={enrollmentCta.href}
                  onClick={() => {
                    fireEvent('educatorPurchaseClick', {
                      cta_location: 'sticky nav',
                      cta: enrollmentCta.label,
                    })
                    fireEvent('educatorSignClick', {
                      action: enrollmentCta.label.toLowerCase(),
                      location: 'sticky nav',
                    })
                  }}
                  className={`cursor-pointer flex h-[36px] items-center justify-center px-[6px] text-xs font-semibold uppercase tracking-[0.6px] ${textColor}`}
                  style={linkColorStyle}
                >
                  {enrollmentCta.label}
                </Link>
              )}

              <Link
                href={loginCta.href}
                onClick={() => {
                  fireEvent('educatorSignClick', {
                    action: loginCta.label.toLowerCase(),
                    location: 'sticky nav',
                  })
                }}
                className={`cursor-pointer flex h-[36px] items-center justify-center border px-[6px] text-xs font-semibold uppercase tracking-[0.6px] ${textColor}`}
                style={{ borderColor: '#696969', ...linkColorStyle }}
              >
                {loginCta.label}
              </Link>

              <div
                className={`${topNavClasses.wrapper} ${!isLight ? topNavClasses.burgerWhite : ''}`}
              >
                <div
                  className='flex h-[36px] w-[44px] items-center justify-center'
                  style={{ border: '1px solid #696969' }}
                >
                  <MobileMenuButton />
                </div>
              </div>
            </div>
          </div>
        </nav>
      </header>

      {!hideDesktopNav && (
        <header
          className={`fixed left-1/2 z-50 hidden w-fit -translate-x-1/2 transition-[opacity,transform] duration-300 ease-in-out md:block ${withBanner ? 'top-[135px]' : 'top-[63px]'} ${
            desktopNavHidden
              ? '-translate-y-[120px] opacity-0 pointer-events-none'
              : ''
          }`}
        >
          <nav className={desktopBg}>
            <div className='flex h-12 items-center justify-between'>
              <div className='flex items-center'>
                <TrackClick
                  event='educatorSignClick'
                  action='masterclass'
                  location='sticky nav'
                >
                  <button
                    type='button'
                    onClick={() =>
                      window.scrollTo({ top: 0, behavior: 'smooth' })
                    }
                    aria-label={t('enrollment.nav.logoAlt')}
                    className='cursor-pointer flex w-[112px] items-center pl-[10px]'
                  >
                    <Image
                      src={`${basePath}/images/executive-logo.svg`}
                      alt={t('enrollment.nav.logoAlt')}
                      width={78}
                      height={23}
                      style={isLight ? { filter: 'brightness(0)' } : undefined}
                    />
                  </button>
                </TrackClick>

                <div className='flex items-center'>
                  {sectionOrder.map((section) => (
                    <TrackClick
                      key={section}
                      event='educatorSignClick'
                      action={section}
                      location='sticky nav'
                    >
                      <button
                        type='button'
                        data-target={section}
                        onClick={handleNavClick}
                        className='cursor-pointer relative flex h-[36px] items-center justify-center px-[10px]'
                      >
                        <span
                          className={`text-xs font-semibold uppercase tracking-[0.6px] ${textColor}`}
                        >
                          {t(`enrollment.nav.${section}`)}
                        </span>
                        {activeSection === section && (
                          <span
                            className={`absolute bottom-1 left-1/2 h-[2px] w-[calc(100%-20px)] -translate-x-1/2 ${activeBg}`}
                          />
                        )}
                      </button>
                    </TrackClick>
                  ))}
                </div>
              </div>

              <div className='flex items-center gap-[7px] whitespace-nowrap pl-[6px] pr-[10px]'>
                <Link
                  href={enrollmentCta.href}
                  onClick={() => {
                    fireEvent('educatorPurchaseClick', {
                      cta_location: 'sticky nav',
                      cta: enrollmentCta.label,
                    })
                    fireEvent('educatorSignClick', {
                      action: enrollmentCta.label.toLowerCase(),
                      location: 'sticky nav',
                    })
                  }}
                  className={`cursor-pointer flex h-[36px] items-center justify-center px-[10px] text-xs font-semibold uppercase tracking-[0.6px] ${textColor}`}
                  style={linkColorStyle}
                >
                  {enrollmentCta.label}
                </Link>
                <Link
                  href={loginCta.href}
                  onClick={() => {
                    fireEvent('educatorSignClick', {
                      action: loginCta.label.toLowerCase(),
                      location: 'sticky nav',
                    })
                  }}
                  className={`cursor-pointer flex h-[36px] items-center justify-center border px-[10px] text-xs font-semibold uppercase tracking-[0.6px] ${borderColor} ${textColor}`}
                  style={linkColorStyle}
                >
                  {loginCta.label}
                </Link>
              </div>
            </div>
          </nav>
        </header>
      )}
    </>
  )
}
